import { Locales } from '@griply/types';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'shared/assets/icons';
import { Alert, Colors as AlertColors } from 'shared/components/ui/alert';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useResetPushRegistrationMutation } from 'shared/hooks/queries/use-reset-push-registration-mutation';
import { usePushDiagnostics } from 'shared/hooks/use-push-diagnostics';
import { useUser } from 'shared/hooks/use-user';
import { getErrorReporter } from 'shared/services/error-tracking';
import { PushRegistrationError } from 'shared/types/electron';
import { getIsElectronTag } from 'shared/utils/get-is-electron-tag';

import { getPushErrorTranslationKey } from './get-push-error-translation-key';
import * as Styled from './notifications-debug-panel.style';

type DebugRowProps = {
  label: string;
  value: string;
};

const DebugRow: React.FC<DebugRowProps> = ({ label, value }) => (
  <Styled.Row>
    <Styled.RowLabel variant="overline">{label}</Styled.RowLabel>
    <Styled.RowValue variant="overline">{value}</Styled.RowValue>
  </Styled.Row>
);

export const NotificationsDebugPanel: React.FC = () => {
  const { t } = useTranslation();
  const user = useUser();
  const {
    permission,
    serviceWorker,
    cachedToken,
    tokenDate,
    liveToken,
    liveTokenError,
    liveTokenMatchesCached,
    tokenDoc,
    isElectron,
    isRefreshing,
    refresh,
  } = usePushDiagnostics(user?.uid);
  const {
    mutate: resetPushRegistration,
    isPending: isResetting,
    isError: resetFailed,
    error: resetError,
  } = useResetPushRegistrationMutation();

  const empty = t('forms.user-settings.sections.notifications.debug.empty');
  const yes = t('forms.user-settings.sections.notifications.debug.values.yes');
  const no = t('forms.user-settings.sections.notifications.debug.values.no');
  const boolValue = (value: boolean | null) =>
    value === null ? empty : value ? yes : no;

  const onRefresh = () => {
    void refresh();
  };

  const onForceReset = () => {
    if (!user?.uid) {
      return;
    }
    const confirmed = window.confirm(
      t('forms.user-settings.sections.notifications.debug.force-reset.confirm'),
    );
    if (!confirmed) {
      return;
    }
    const reportResetFailure = (error: unknown) => {
      getErrorReporter().captureException(error, {
        tags: {
          source: 'reset-push-registration',
          op: 'force-reset',
          is_electron: getIsElectronTag(),
          ...(error instanceof PushRegistrationError && { code: error.code }),
        },
      });
    };

    resetPushRegistration(
      {
        userId: user.uid,
        locale: user.settings?.language ?? Locales.EnUS,
      },
      { onError: reportResetFailure },
    );
  };

  return (
    <Styled.Container>
      <Typography variant="subtitle2" component="span">
        {t('forms.user-settings.sections.notifications.debug.title')}
      </Typography>
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.permission',
        )}
        value={permission}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.service-worker',
        )}
        value={serviceWorker}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.cached-token',
        )}
        value={cachedToken ?? empty}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.token-date',
        )}
        value={tokenDate ? tokenDate.toLocaleString() : empty}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.live-token',
        )}
        value={liveToken ?? empty}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.live-token-error',
        )}
        value={liveTokenError ?? empty}
      />
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.live-token-match',
        )}
        value={boolValue(liveTokenMatchesCached)}
      />
      <DebugRow
        label={t('forms.user-settings.sections.notifications.debug.rows.doc')}
        value={t(
          tokenDoc
            ? 'forms.user-settings.sections.notifications.debug.values.exists'
            : 'forms.user-settings.sections.notifications.debug.values.missing',
        )}
      />
      {!!tokenDoc && (
        <>
          <DebugRow
            label={t(
              'forms.user-settings.sections.notifications.debug.rows.doc-timezone',
            )}
            value={tokenDoc.timezone ?? empty}
          />
          <DebugRow
            label={t(
              'forms.user-settings.sections.notifications.debug.rows.doc-platform',
            )}
            value={tokenDoc.platform ?? empty}
          />
          <DebugRow
            label={t(
              'forms.user-settings.sections.notifications.debug.rows.doc-version',
            )}
            value={tokenDoc.version ?? empty}
          />
          <DebugRow
            label={t(
              'forms.user-settings.sections.notifications.debug.rows.doc-updated-at',
            )}
            value={
              tokenDoc.updatedAt instanceof Date
                ? tokenDoc.updatedAt.toLocaleString()
                : empty
            }
          />
        </>
      )}
      <DebugRow
        label={t(
          'forms.user-settings.sections.notifications.debug.rows.electron',
        )}
        value={boolValue(isElectron)}
      />
      {resetFailed && (
        <Alert startIcon={Info} color={AlertColors.Error}>
          <Typography variant="overline" component="span">
            {t(getPushErrorTranslationKey(resetError))}
          </Typography>
        </Alert>
      )}
      <Styled.Actions>
        <Button
          variant={Variants.Outlined}
          color={Colors.Primary}
          size={Sizes.Small}
          disabled={isRefreshing || isResetting}
          onClick={onRefresh}
        >
          {t('forms.user-settings.sections.notifications.debug.refresh.label')}
        </Button>
        <Button
          variant={Variants.Outlined}
          color={Colors.Error}
          size={Sizes.Small}
          disabled={isResetting}
          onClick={onForceReset}
        >
          {t(
            'forms.user-settings.sections.notifications.debug.force-reset.label',
          )}
        </Button>
      </Styled.Actions>
    </Styled.Container>
  );
};
